import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { THEME_FONT_KEYS, type ThemeFontKey, type ThemeTokens } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { tenancyApi } from '@/services/tenancy.api';
import { ApiError } from '@/services/api-client';
import { applyTheme } from '@/theme/theme-provider';
import { Alert, Button, ColorField, Field } from '@/components/ui';

const FONT_LABEL: Record<ThemeFontKey, string> = {
  system: 'System default',
  inter: 'Inter',
  poppins: 'Poppins',
  roboto: 'Roboto',
  'open-sans': 'Open Sans',
  lato: 'Lato',
  nunito: 'Nunito',
  'work-sans': 'Work Sans',
  'source-sans': 'Source Sans',
  merriweather: 'Merriweather',
};

/**
 * Create or edit a custom theme. The default theme never reaches this form
 * for editing — "cloneable but not editable in place" — but it (like any
 * theme) can be the source for `cloneFrom`.
 *
 * Every field change applies straight to the real running shell via
 * `applyTheme()`, the same function `ThemeProvider` uses for the actual
 * active theme — so "preview" is never a mockup, it is the app reacting.
 * Leaving this page (save, cancel, or navigating away) restores whatever
 * theme was really active.
 */
export function AppearanceFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const cloneFromId = !id ? searchParams.get('cloneFrom') : null;
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const themeQuery = useQuery({
    queryKey: ['tenancy', 'themes', id],
    queryFn: () => tenancyApi.listThemes(),
    enabled: Boolean(id) || Boolean(cloneFromId),
  });
  const activeThemeQuery = useQuery({
    queryKey: ['tenancy', 'themes', 'active'],
    queryFn: ({ signal }) => tenancyApi.getActiveTheme(signal),
  });

  const existing = id ? themeQuery.data?.find((theme) => theme.id === id) : undefined;
  const cloneSource = cloneFromId
    ? themeQuery.data?.find((theme) => theme.id === cloneFromId)
    : undefined;

  const [name, setName] = useState<string | null>(null);
  const [tokens, setTokens] = useState<ThemeTokens | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A brand-new theme (no id, no clone) starts from whatever is actually
  // active right now — a sensible default, and always available since
  // `activeThemeQuery` runs unconditionally.
  const seedTokens =
    existing?.tokens ?? cloneSource?.tokens ?? activeThemeQuery.data?.tokens ?? null;
  const effectiveName =
    name ?? existing?.name ?? (cloneSource ? `${cloneSource.name} (copia)` : '');
  const effectiveTokens = tokens ?? seedTokens;

  // Live preview: apply the draft to the real shell while this form is
  // open, and always restore the real active theme on the way out.
  useEffect(() => {
    if (effectiveTokens) applyTheme(effectiveTokens);
    return () => {
      if (activeThemeQuery.data) applyTheme(activeThemeQuery.data.tokens);
    };
  }, [effectiveTokens]);

  const save = useMutation({
    mutationFn: () => {
      if (!effectiveTokens) throw new Error('No tokens to save');
      const payload = { name: effectiveName.trim(), tokens: effectiveTokens };
      return id ? tenancyApi.updateTheme(id, payload) : tenancyApi.createTheme(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenancy', 'themes'] });
      void navigate('/administration/appearance');
    },
    onError: (error: unknown) => {
      setSaveError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  function setToken<K extends keyof ThemeTokens>(key: K, value: ThemeTokens[K]): void {
    if (!effectiveTokens) return;
    setTokens({ ...effectiveTokens, [key]: value });
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (!effectiveName.trim() || !effectiveTokens) return;
    save.mutate();
  }

  function handleCancel(): void {
    if (activeThemeQuery.data) applyTheme(activeThemeQuery.data.tokens);
    void navigate('/administration/appearance');
  }

  if (id && themeQuery.isPending) return null;
  if (id && existing?.isDefault) {
    return (
      <div className="page">
        <Alert tone="warning">{t('administration.appearance.defaultNotEditable')}</Alert>
      </div>
    );
  }
  if (!effectiveTokens) return null;

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">
          {id ? t('administration.appearance.editTitle') : t('administration.appearance.newTitle')}
        </h1>
      </header>

      <form className="settings-card" onSubmit={handleSubmit}>
        {saveError && <Alert tone="danger">{saveError}</Alert>}

        <Field
          label={t('administration.appearance.name')}
          name="themeName"
          value={effectiveName}
          onChange={(event) => setName(event.target.value)}
          required
        />

        <div className="form-grid">
          <ColorField
            label={t('administration.appearance.headerBg')}
            value={effectiveTokens.headerBg}
            onChange={(hex) => setToken('headerBg', hex)}
          />
          <ColorField
            label={t('administration.appearance.sidebarBg')}
            value={effectiveTokens.sidebarBg}
            onChange={(hex) => setToken('sidebarBg', hex)}
          />
          <ColorField
            label={t('administration.appearance.footerBg')}
            value={effectiveTokens.footerBg}
            onChange={(hex) => setToken('footerBg', hex)}
          />
          <ColorField
            label={t('administration.appearance.primaryColor')}
            value={effectiveTokens.primaryColor}
            onChange={(hex) => setToken('primaryColor', hex)}
          />
          <ColorField
            label={t('administration.appearance.secondaryColor')}
            value={effectiveTokens.secondaryColor}
            onChange={(hex) => setToken('secondaryColor', hex)}
          />
          <ColorField
            label={t('administration.appearance.tertiaryColor')}
            value={effectiveTokens.tertiaryColor}
            onChange={(hex) => setToken('tertiaryColor', hex)}
          />
          <ColorField
            label={t('administration.appearance.textColor')}
            value={effectiveTokens.textColor}
            onChange={(hex) => setToken('textColor', hex)}
          />

          <div className="field">
            <label className="field-label" htmlFor="themeFont">
              {t('administration.appearance.fontFamily')}
            </label>
            <select
              id="themeFont"
              className="field-input"
              value={effectiveTokens.fontFamily}
              onChange={(event) => setToken('fontFamily', event.target.value as ThemeFontKey)}
            >
              {THEME_FONT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {FONT_LABEL[key]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="card-fields-row">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('common.actions.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
